import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { Grid, Accordion, Button, Typography } from '@material-ui/core';
import { withStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import Notes from './components/Notes';
import TableLoader from '../../components/TableLoader';
import * as notesActionsImport from '../../redux/actions/notes';
import NoteAdd from './components/NoteAdd';
import NotesHeader from './components/NotesHeader';
import { notificationsUnread } from '../../helpers/pricing';
import { hasPricingNotesAccess } from '../../helpers/user';
import { NOTES_PANEL } from '../../helpers/constants';
import PricingNoteForm from './components/PricingNoteForm';
import GeneralNoteForm from './components/GeneralNoteForm';
import { createNotes, createStandardizeNotes } from '../../helpers/api';

const styles = makeStyles(() => ({
  notesDetails: {
    backgroundColor: 'none',
  },
  notesHeader: {
    backgroundColor: '#F4F4F4',
  },
  notesHeaderDark: {
    backgroundColor: '#2D3436',
  },
  addNoteItem: {
    borderTop: '1px solid lightgray',
    paddingBottom: '16px !important',
    paddingLeft: '16px !important',
    paddingTop: '8px !important',
    paddingRight: '8px !important',
  },
}));

const AccordionSummary = withStyles({
  root: {
    margin: '0px !important',
    padding: 0,
    '& .MuiAccordionSummary-content': {
      marginBottom: '5px !important',
      '&.Mui-expanded': {
        marginTop: '20px !important',
        marginBottom: '5px !important',
      },
    },
    '&.Mui-expanded': {
      margin: '0px !important',
    },
    borderBottom: '1px solid lightgray',
  },
})(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    margin: '0px 0px 0px 0px',
    padding: '0px 0px 0px 0px',
  },
})(MuiAccordionDetails);

const NotesContainer = props => {
  const theme = useTheme();
  const { notes, eventId, user, eventActions } = props;
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [newNotes, setNewNotes] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isChipsClicked, setIsChipsClicked] = useState(false);
  const [totalNotes, setTotalNotes] = useState(0);
  const [showOnlyPinned, setShowOnlyPinned] = useState(false);
  const [visibleNotes, setVisibleNotes] = useState([]);
  const [chatNotes, setChatNotes] = useState([]);
  const [notesPanel, setNotesPanel] = useState(NOTES_PANEL.NOTES);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [formSubmitHandler, setFormSubmitHandler] = useState(null);
  const history = useHistory();
  const classes = styles();
  const entityType = 'EVENT';
  const noteType = 'PRICING';
  const sourceType = 'COGNITO';

  const fetchData = async () => {
    const notesResponse = await notesActionsImport.fetchNotesApi(entityType, eventId);
    setIsTableLoading(false);
    eventActions.notesReceived(notesResponse);
  };

  const handlePinnedNotes = event => {
    const showOnlyPinnedSelected = event.target.checked;
    setShowOnlyPinned(showOnlyPinnedSelected);
    if (notes && notes.length > 0) {
      if (showOnlyPinnedSelected) {
        setVisibleNotes(chatNotes.filter(note => note.pinned));
      } else {
        setVisibleNotes(chatNotes);
      }
    }
  };

  const useFetch = () => {
    useEffect(() => {
      fetchData();
    }, []);
  };

  useEffect(() => {
    fetchData();
  }, [history.location.pathname]);

  const handleAccordionClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleFormReady = submitFn => {
    setFormSubmitHandler(() => submitFn);
  };

  const handleNoteSubmit = async formData => {
    const isStandardizeNote = formData.standardizeNote || false;
    setNotesPanel(NOTES_PANEL.NOTES);
    try {
      if (isStandardizeNote) {
        setIsLoading(true);
        const data = { ...formData, entityID: eventId, entityType, noteType, sourceType, userVersion: 0 };
        await createStandardizeNotes(data);
        const notesResponse = await notesActionsImport.fetchNotesApi(entityType, eventId);
        eventActions.notesReceived(notesResponse);
      } else {
        setIsLoading(true);
        await createNotes({
          sourceType,
          entityType,
          entityID: eventId,
          noteType,
          userVersion: 0,
          note: formData.note,
        });
        const notesResponse = await notesActionsImport.fetchNotesApi(entityType, eventId);
        eventActions.notesReceived(notesResponse);
      }
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Error submitting note:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setTotalNotes((notes && notes.length) || 0);
    const notesUnread = notificationsUnread(notes, user);
    setNewNotes(notesUnread);
    if (notes && notes.length > 0) {
      const sortedNotes = notes.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      setChatNotes(sortedNotes);
      if (showOnlyPinned) {
        setVisibleNotes(sortedNotes.filter(note => note.pinned));
      } else {
        setVisibleNotes(sortedNotes);
      }
    } else {
      setChatNotes(notes);
      setVisibleNotes(notes);
    }
  }, [notes]);

  useFetch();

  if (isTableLoading) {
    return <TableLoader />;
  }

  return (
    <Accordion expanded={isExpanded}>
      <AccordionSummary className={theme.palette.type === 'light' ? classes.notesHeader : classes.notesHeaderDark}>
        <NotesHeader
          setIsChipsClicked={setIsChipsClicked}
          setIsExpanded={setIsExpanded}
          notes={notes}
          user={user}
          handleAccordionClick={handleAccordionClick}
          isExpanded={isExpanded}
          newNotes={newNotes}
          totalNotes={totalNotes}
          showOnlyPinned={showOnlyPinned}
          handlePinnedNotes={handlePinnedNotes}
          notesPanel={notesPanel}
          setNotesPanel={setNotesPanel}
        />
      </AccordionSummary>
      <AccordionDetails className={classes.notesDetails}>
        <Grid container>
          {notesPanel === NOTES_PANEL.NOTES && (
            <>
              <Grid item xs={12}>
                <Notes
                  fetchData={fetchData}
                  isChipsClicked={isChipsClicked}
                  isLoading={isLoading}
                  newNotes={newNotes}
                  notes={visibleNotes}
                  showOnlyPinned={showOnlyPinned}
                  setNewNotes={setNewNotes}
                  setVisibleNotes={setVisibleNotes}
                  user={user}
                />
              </Grid>
              <Grid item className={classes.addNoteItem} xs={12}>
                {hasPricingNotesAccess() ? (
                  <Grid container spacing={1} direction="row" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Grid item xs={6} style={{ display: 'flex', flex: 1, minWidth: 0, maxWidth: 180 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setNotesPanel(NOTES_PANEL.PRICING)}
                        startIcon={<AddIcon fontSize="small" />}
                        size="small"
                        fullWidth
                        style={{ whiteSpace: 'nowrap', minWidth: 0, padding: '4px 8px' }}
                      >
                        <Typography style={{ fontSize: '11px' }}>ADD PRICING NOTE</Typography>
                      </Button>
                    </Grid>
                    <Grid item xs={6} style={{ display: 'flex', flex: 1, minWidth: 0, maxWidth: 180 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setNotesPanel(NOTES_PANEL.GENERAL)}
                        startIcon={<AddIcon fontSize="small" />}
                        size="small"
                        fullWidth
                        style={{ whiteSpace: 'nowrap', minWidth: 0, padding: '4px 8px' }}
                      >
                        <Typography style={{ fontSize: '11px' }}>ADD GENERAL NOTE</Typography>
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <NoteAdd
                    entityID={eventId}
                    entityType={entityType}
                    eventActions={eventActions}
                    isLoading={isLoading}
                    noteType={noteType}
                    setIsLoading={setIsLoading}
                    sourceType={sourceType}
                  />
                )}
              </Grid>
            </>
          )}
          {notesPanel === NOTES_PANEL.PRICING && (
            <Grid item xs={12}>
              <PricingNoteForm
                setSubmitEnabled={setSubmitEnabled}
                onSubmit={handleNoteSubmit}
                onFormReady={handleFormReady}
                eventId={eventId}
              />
            </Grid>
          )}
          {notesPanel === NOTES_PANEL.GENERAL && (
            <Grid item xs={12}>
              <GeneralNoteForm
                setSubmitEnabled={setSubmitEnabled}
                onSubmit={handleNoteSubmit}
                onFormReady={handleFormReady}
              />
            </Grid>
          )}
          {notesPanel !== NOTES_PANEL.NOTES && (
            <Grid item className={classes.addNoteItem} xs={12}>
              <Grid container spacing={2} direction="row" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Grid item md="auto">
                  <Button
                    variant="outlined"
                    onClick={() => setNotesPanel(NOTES_PANEL.NOTES)}
                    size="small"
                    fullWidth
                    style={{ borderColor: '#A8A8A8', backgroundColor: 'transparent' }}
                  >
                    <Typography style={{ fontSize: '12px', color: '#A8A8A8' }}>CANCEL</Typography>
                  </Button>
                </Grid>
                <Grid item md="auto">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (formSubmitHandler) {
                        formSubmitHandler();
                      }
                    }}
                    disabled={!submitEnabled}
                    size="small"
                    fullWidth
                  >
                    <Typography style={{ fontSize: '12px' }}>ADD NOTE</Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

NotesContainer.propTypes = {
  eventActions: PropTypes.object.isRequired,
  eventId: PropTypes.string.isRequired,
  notes: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  notes: state.notes.data.notes,
  user: state.user,
});

const mapDispatchToProps = dispatch => ({ eventActions: bindActionCreators(notesActionsImport, dispatch) });

export default connect(mapStateToProps, mapDispatchToProps)(NotesContainer);
